import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useState } from "react";
import {
  MoonStar, LayoutDashboard, Users, Package, Plane, BookOpenCheck, CreditCard,
  UserRound, Network, Sparkles, Globe, LogOut, Menu, Bell, Building2, Compass, Briefcase
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV_GROUPS = [
  {
    label: "OPERATIONS",
    items: [
      { to: "/app", icon: LayoutDashboard, key: "app.dashboard", end: true, testid: "sidebar-dashboard" },
      { to: "/app/portal", icon: Compass, key: "app.portal", testid: "sidebar-portal" },
      { to: "/app/leads", icon: Users, key: "app.leads", testid: "sidebar-leads" },
      { to: "/app/packages", icon: Package, key: "app.packages", testid: "sidebar-packages" },
      { to: "/app/departures", icon: Plane, key: "app.departures", testid: "sidebar-departures" },
      { to: "/app/bookings", icon: BookOpenCheck, key: "app.bookings", testid: "sidebar-bookings" },
      { to: "/app/payments", icon: CreditCard, key: "app.payments", testid: "sidebar-payments" },
    ],
  },
  {
    label: "NETWORK",
    items: [
      { to: "/app/customers", icon: UserRound, key: "app.customers", testid: "sidebar-customers" },
      { to: "/app/agents", icon: Network, key: "app.agents", testid: "sidebar-agents" },
      { to: "/app/agent", icon: Briefcase, key: "app.agent_portal", testid: "sidebar-agent-portal" },
    ],
  },
  {
    label: "INTELLIGENCE",
    items: [
      { to: "/app/ai", icon: Sparkles, key: "app.ai", testid: "sidebar-ai" },
    ],
  },
];

const NavItems = ({ t, onNavigate, groups }) => (
  <nav className="flex-1 px-3 py-4 overflow-y-auto">
    {groups.map((group) => (
      <div key={group.label} className="mb-5">
        <div className="px-3 mb-1.5 text-[9px] font-mono uppercase tracking-[0.25em] text-slate-400">{group.label}</div>
        <div className="space-y-0.5">
          {group.items.map(({ to, icon: Icon, key, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid} onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive
                  ? "bg-emerald-50 text-emerald-900 font-medium shadow-[inset_2px_0_0_0_#047857]"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`
              }>
              <Icon className="w-[18px] h-[18px]" />
              {t(key)}
            </NavLink>
          ))}
        </div>
      </div>
    ))}
  </nav>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const role = user?.role;
  const groups = role === "agent"
    ? [{ label: "AGENT", items: [{ to: "/app/agent", icon: Briefcase, key: "app.agent_portal", testid: "sidebar-agent-portal" }] }]
    : role === "customer"
      ? [{ label: "PORTAL", items: [{ to: "/app/portal", icon: Compass, key: "app.portal", testid: "sidebar-portal" }] }]
      : NAV_GROUPS;
  const bottomItems = role === "agent"
    ? [{ to: "/app/agent", icon: Briefcase, key: "app.agent_portal", testid: "bottomnav-agent" }]
    : role === "customer"
      ? [{ to: "/app/portal", icon: Compass, key: "app.portal", testid: "bottomnav-portal" }]
      : [
          { to: "/app", icon: LayoutDashboard, key: "app.dashboard", end: true, testid: "bottomnav-dashboard" },
          { to: "/app/leads", icon: Users, key: "app.leads", testid: "bottomnav-leads" },
          { to: "/app/bookings", icon: BookOpenCheck, key: "app.bookings", testid: "bottomnav-bookings" },
          { to: "/app/payments", icon: CreditCard, key: "app.payments", testid: "bottomnav-payments" },
          { to: "/app/ai", icon: Sparkles, key: "app.ai", testid: "bottomnav-ai" },
        ];

  return (
    <div className="min-h-screen bg-sand flex">
      <aside className="hidden lg:flex w-64 flex-col bg-white text-slate-900 fixed inset-y-0 z-40 border-r border-sand-border">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-sand-border">
          <img src="/logo.jpeg" alt="Travel Hub Desk" className="w-10 h-10 rounded-lg object-cover" />
          <div>
            <div className="font-serif text-base leading-none tracking-tight text-slate-900">Travel Hub Desk</div>
          </div>
        </div>
        <NavItems t={t} groups={groups} />
        <div className="p-3 border-t border-sand-border">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-50 border border-sand-border">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-emerald-800 text-white text-xs font-semibold border border-emerald-800">
                {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-700">{user?.role?.replace(/_/g, " ")}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/70 sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-lg hover:bg-sand" data-testid="mobile-sidebar-trigger">
                  <Menu className="w-5 h-5 text-brand-800" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-white text-slate-900 border-sand-border">
                <div className="h-16 flex items-center gap-2.5 px-5 border-b border-sand-border">
                  <img src="/logo.jpeg" alt="Travel Hub Desk" className="w-9 h-9 rounded-lg object-cover" />
                  <div className="leading-none">
                    <span className="font-serif text-base text-slate-900">Travel Hub Desk</span>
                  </div>
                </div>
                <NavItems t={t} groups={groups} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sand-border bg-white text-[11px] font-mono uppercase tracking-[0.15em] text-brand-800" data-testid="tenant-pill">
              <Building2 className="w-3.5 h-3.5 text-gold-600" />
              {user?.tenant_name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="notification-bell" className="relative p-2 rounded-full hover:bg-sand transition-colors">
              <Bell className="w-[18px] h-[18px] text-brand-800/70" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-gold-500 border border-white" />
            </button>
            <button data-testid="language-toggle-button" onClick={toggle}
              className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-brand-800/70 hover:text-gold-600 transition-colors px-3 py-1.5 rounded-full border border-sand-border bg-white hover:border-gold-500/40">
              <Globe className="w-3.5 h-3.5" />{lang === "en" ? "BM" : "EN"}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-sand transition-colors">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-brand-800 text-sand text-xs font-semibold">
                      {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-brand-950 hidden sm:block max-w-[140px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gold-600 mt-1">{user?.role}</div>
                </div>
                <DropdownMenuItem data-testid="logout-button" onClick={handleLogout} className="text-destructive cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />{t("app.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>

        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-sand-border flex justify-around px-2 py-1.5" data-testid="mobile-bottom-nav">
          {bottomItems.map(({ to, icon: Icon, key, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl min-w-[56px] transition-colors ${isActive ? "text-emerald-800" : "text-slate-400"}`
              }>
              <Icon className="w-5 h-5" />
              <span className="text-[9px] font-mono uppercase tracking-wide">{t(key)}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
